import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import { useAppearance } from '../../contexts/AppearanceContext';
import { useAuth } from '../../contexts/AuthContext';
import { LOOKS, type Look, type ThemePref } from '../../lib/appearance';
import { setLanguage } from '../../i18n';
import { useLanguageList } from '../../hooks/useLanguageList';

/**
 * User menu → Appearance. Language and theme (Light / Dark / Auto) are everyone's own, kept in this browser. The
 * look is an admin choice for everybody: a workspace owner/admin sets this workspace's, a global admin sets the
 * system default. Everyone else sees which look is in force and who chose it.
 */
const LOOK_KEYS = {
  calm: { name: 'appearance.look.calm', hint: 'appearance.look.calmHint' },
  familiar: { name: 'appearance.look.familiar', hint: 'appearance.look.familiarHint' },
  focus: { name: 'appearance.look.focus', hint: 'appearance.look.focusHint' },
} as const satisfies Record<Look, { name: string; hint: string }>;

const THEMES = [
  { value: 'light', label: 'appearance.theme.light' },
  { value: 'dark', label: 'appearance.theme.dark' },
  { value: 'auto', label: 'appearance.theme.auto', hint: 'appearance.theme.autoHint' },
] as const satisfies ReadonlyArray<{ value: ThemePref; label: string; hint?: string }>;

export function AppearanceDialog({ onClose }: { onClose: () => void }) {
  const { look, themePref, setThemePref, view, tenant, setSystemLook, setTenantLook } = useAppearance();
  const { isGlobalAdmin, canIn } = useAuth();
  const { t, i18n } = useTranslation();
  const languages = useLanguageList();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSetWorkspace = !!tenant && canIn('manageAppearance', tenant.id);
  const systemLook = view?.system ?? null;

  const save = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('appearance.look.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const lookName = (l: Look) => t(LOOK_KEYS[l].name);
  const source =
    view?.source === 'tenant'
      ? tenant
        ? t('appearance.look.sourceTenant', { name: tenant.name })
        : t('appearance.look.sourceThisWorkspace')
      : view?.source === 'system'
        ? t('appearance.look.sourceSystem')
        : t('appearance.look.sourceDefault');

  return (
    <Modal title={t('appearance.title')} description={t('appearance.description')} onClose={onClose} locked={busy}>
      <div className="px-5 py-4 space-y-5">
        <Section title={t('appearance.language.title')}>
          <div className="grid grid-cols-2 gap-2">
            {languages.map((l) => (
              <Choice
                key={l.code}
                selected={i18n.resolvedLanguage === l.code}
                onClick={() => void setLanguage(l.code)}
                label={l.name}
                lang={l.code}
              />
            ))}
          </div>
        </Section>

        <Section title={t('appearance.theme.title')}>
          <div className="grid grid-cols-3 gap-2">
            {THEMES.map((th) => (
              <Choice
                key={th.value}
                selected={themePref === th.value}
                onClick={() => setThemePref(th.value)}
                label={t(th.label)}
                hint={'hint' in th ? t(th.hint) : undefined}
              />
            ))}
          </div>
        </Section>

        <Section title={t('appearance.look.title')} note={t('appearance.look.now', { look: lookName(look), source })}>
          {canSetWorkspace ? (
            <div className="space-y-2">
              <p className="text-xs text-muted">{t('appearance.look.forWorkspace', { name: tenant!.name })}</p>
              <div className="grid grid-cols-2 gap-2">
                <Choice
                  selected={view?.tenant == null}
                  disabled={busy}
                  onClick={() => save(() => setTenantLook(null))}
                  label={t('appearance.look.systemDefault')}
                  hint={lookName(systemLook ?? 'calm')}
                />
                {LOOKS.map((l) => (
                  <Choice
                    key={l}
                    selected={view?.tenant === l}
                    disabled={busy}
                    onClick={() => save(() => setTenantLook(l))}
                    label={lookName(l)}
                    hint={t(LOOK_KEYS[l].hint)}
                  />
                ))}
              </div>
            </div>
          ) : (
            !isGlobalAdmin && <p className="text-xs text-muted">{t('appearance.look.adminsOnly')}</p>
          )}
        </Section>

        {isGlobalAdmin && (
          <Section title={t('appearance.system.title')} note={t('appearance.system.note')}>
            <div className="grid grid-cols-3 gap-2">
              {LOOKS.map((l) => (
                <Choice
                  key={l}
                  selected={(systemLook ?? 'calm') === l}
                  disabled={busy}
                  onClick={() => save(() => setSystemLook(l))}
                  label={lookName(l)}
                  hint={t(LOOK_KEYS[l].hint)}
                />
              ))}
            </div>
          </Section>
        )}

        {error && (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
      </div>
      <div className="px-5 py-3 border-t border-line flex justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="min-h-11 px-4 text-sm font-medium text-ink bg-surface border border-line rounded-control hover:bg-surface-2 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {t('common.done')}
        </button>
      </div>
    </Modal>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {note && <p className="text-xs text-muted mt-0.5 mb-2">{note}</p>}
      {!note && <div className="mb-2" />}
      {children}
    </section>
  );
}

function Choice({
  selected,
  onClick,
  label,
  hint,
  disabled,
  lang,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  hint?: string;
  disabled?: boolean;
  /** A language's own name, so screen readers say it in that language. */
  lang?: string;
}) {
  return (
    <button
      type="button"
      lang={lang}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={`min-h-11 px-3 py-2 flex-col items-start justify-center text-left rounded-control border transition-colors disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        selected ? 'bg-tint text-on-tint border-transparent' : 'bg-surface text-ink border-line hover:bg-surface-2'
      }`}
    >
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className={`text-xs ${selected ? '' : 'text-muted'}`}>{hint}</span>}
    </button>
  );
}
